import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import api from "@/lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { useI18n } from "@/context/I18nContext";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const { loadCart } = useCart();
  const { t } = useI18n();
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); loadCart(); return; }
        if (data.status === "expired") { setStatus("expired"); return; }
        if (attempts.current >= 6) { setStatus("timeout"); return; }
        attempts.current += 1;
        timer = setTimeout(poll, 2000);
      } catch { setStatus("error"); }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <Layout hideAd>
      <div className="max-w-lg mx-auto text-center py-24 px-4">
        {status === "checking" && (<><Loader2 className="w-14 h-14 mx-auto text-primary animate-spin mb-4" /><h1 className="font-display font-bold text-2xl">{t("confirmingPayment")}</h1></>)}
        {status === "paid" && (<>
          <CheckCircle2 className="w-16 h-16 mx-auto text-green-600 mb-4" data-testid="payment-success-icon" />
          <h1 className="font-display font-bold text-3xl">{t("paymentSuccessful")}</h1>
          <p className="text-muted-foreground mt-2">{t("thankYouShopping")}</p>
          <div className="flex gap-3 justify-center mt-6">
            <Link to="/account"><Button className="rounded-full" data-testid="view-orders-btn">{t("viewMyOrders")}</Button></Link>
            <Link to="/products"><Button variant="outline" className="rounded-full">{t("continueShopping")}</Button></Link>
          </div>
        </>)}
        {["error", "expired", "timeout"].includes(status) && (<>
          <XCircle className="w-16 h-16 mx-auto text-destructive mb-4" />
          <h1 className="font-display font-bold text-2xl">{status === "timeout" ? t("paymentPendingTitle") : t("paymentIssueTitle")}</h1>
          <p className="text-muted-foreground mt-2">{t("couldntConfirm")}</p>
          <Link to="/cart"><Button className="rounded-full mt-6">{t("backToCart")}</Button></Link>
        </>)}
      </div>
    </Layout>
  );
}
